import type { AuLabels } from '@au/ui';
import type { FluxUnit } from '@au/time';
import type { I18n } from '@lingui/core';
import { msg } from '@lingui/macro';

/**
 * El género de cada unidad, para el artículo de «Últimas 3 semanas». Copia de `UNIT_IS_FEMININE` de
 * `src/au/ui/labels.ts`, que no lo exporta (y `src/au` no se edita). En inglés no cambia nada: los dos
 * mensajes se traducen igual.
 */
const UNIT_IS_FEMININE: Record<FluxUnit, boolean> = {
  m: false,
  h: true,
  d: false,
  w: true,
  mo: false,
  y: false,
};

/**
 * El catálogo de textos de `@au` traducido con Lingui (`src/au/LABELS.md § Montar el provider`).
 *
 * Cada msgid es el texto de `esLabels`, así en español sale exactamente lo mismo que sin provider
 * (lo verifica `au-labels.test.ts`). Las entradas función repiten la lógica de `esLabels` y traducen
 * cada pieza; los textos que `Intl` ya formateó (`dateText`, `text`) llegan como parámetro.
 *
 * Se arma con el `i18n` activo: cambiar de idioma exige armarlo de nuevo (`AppAuLabelsProvider`).
 */
export function buildAuLabels(i18n: I18n): AuLabels {
  const _ = i18n._.bind(i18n);

  return {
    select: {
      placeholder: _(msg`Seleccionar…`),
    },
    combobox: {
      placeholder: _(msg`Buscar…`),
      empty: _(msg`Sin resultados.`),
      clear: _(msg`Limpiar selección`),
      open: _(msg`Abrir lista`),
      remove: (label) => _(msg`Quitar ${label}`),
    },
    numberField: {
      decrement: _(msg`Restar`),
      increment: _(msg`Sumar`),
      roleDescription: _(msg`Campo numérico`),
    },
    slider: {
      thumb: (index, count) => {
        if (count === 2) return index === 0 ? _(msg`Mínimo`) : _(msg`Máximo`);
        const position = index + 1;
        return _(msg`Valor ${position}`);
      },
      valueText: (formatted) => formatted,
    },
    chip: {
      remove: _(msg`Quitar`),
    },
    dialog: {
      close: _(msg`Cerrar`),
    },
    pagination: {
      navigation: _(msg`Paginación`),
      rowsPerPage: _(msg`Filas por página`),
      first: _(msg`Primera página`),
      previous: _(msg`Página anterior`),
      next: _(msg`Página siguiente`),
      last: _(msg`Última página`),
      page: (page, total) => _(msg`Página ${page} de ${total}`),
    },
    callout: {
      dismiss: _(msg`Descartar aviso`),
    },
    toast: {
      dismiss: _(msg`Descartar`),
      viewport: _(msg`Notificaciones`),
    },
    calendar: {
      previous: _(msg`Ir al mes anterior`),
      next: _(msg`Ir al mes siguiente`),
      monthDropdown: _(msg`Elegir el mes`),
      yearDropdown: _(msg`Elegir el año`),
      nav: _(msg`Navegación del calendario`),
      weekNumberHeader: _(msg`Número de semana`),
      weekNumber: (weekNumber) => _(msg`Semana ${weekNumber}`),
      dayButton: (dateText, { today, selected }) => {
        const label = today ? _(msg`Hoy, ${dateText}`) : dateText;
        return selected ? _(msg`${label}, seleccionado`) : label;
      },
      gridcell: (dateText, { today }) => (today ? _(msg`Hoy, ${dateText}`) : dateText),
    },
    timeQuery: {
      dialogTitle: _(msg`Consulta temporal`),
      customRangePlaceholder: _(msg`Rango personalizado: 3h, 3 horas, 3 m…`),
      customRangeLabel: _(msg`Rango personalizado`),
      durationHint: _(msg`Escriba una duración: 3h, 15m, 2w.`),
      from: _(msg`Desde`),
      to: _(msg`Hasta`),
      // Ejemplos de formato, no texto: iguales en todos los idiomas.
      fromPlaceholder: '2026-08-19 14:00',
      toPlaceholder: '2026-08-20 09:30',
      apply: _(msg`Aplicar`),
      rangeError: _(msg`«Hasta» tiene que ser posterior a «Desde».`),
      openEnd: _(msg`ahora`),
      aggregation: _(msg`Agregación`),
      aggregationTrigger: _(msg`Función de agregación`),
      aggShort: {
        avg: _(msg`prom`),
        max: _(msg`máx`),
        min: _(msg`mín`),
        count: _(msg`cuenta`),
        integral: _(msg`int`),
      },
      aggLabel: {
        avg: _(msg`Promedio`),
        max: _(msg`Máximo`),
        min: _(msg`Mínimo`),
        count: _(msg`Cuenta`),
        integral: _(msg`Integral`),
      },
      windowAuto: _(msg`Automática`),
      windowNone: _(msg`Sin agregar`),
      windowNoneShort: _(msg`sin agregar`),
      lastN: (duration, text) => {
        const feminine = UNIT_IS_FEMININE[duration.unit];
        // «Última hora» y no «Última 1 hora»: con uno el numeral sobra. `Intl` no da el nombre de la
        // unidad suelto, así que se recorta del texto que ya vino formateado (como `esLabels`).
        if (duration.value === 1) {
          const unit = text.replace(/^\D*\d+\s*/, '');
          return feminine ? _(msg`Última ${unit}`) : _(msg`Último ${unit}`);
        }
        return feminine ? _(msg`Últimas ${text}`) : _(msg`Últimos ${text}`);
      },
    },
  };
}
